import React, { createContext, ReactElement, ReactNode, useContext, useMemo, useRef, useSyncExternalStore } from "react";

import ErrorNotification from "./ErrorNotification";
import SuccessNotification from "./SuccessNotification";
import CustomNotification from "./CustomNotification";
import { NotificationOptions } from "./Notification";

// ---=== Type Definitions ===--- //

export type NotificationType = "error" | "success" | "custom";

export interface ManagedNotification {
    component: ReactElement;
    options?: NotificationOptions;
    id: string;
    message?: string | object | object[];
    type: NotificationType;
    timerId?: number;
    fadeTimeoutId?: number;
    isRemoving?: boolean;
}

export interface NotificationManagerAPI {
    addCustom: (message: string | object | object[], options?: NotificationOptions) => string;
    addError: (message: string | object | object[], options?: NotificationOptions) => string;
    addSuccess: (message: string | object | object[], options?: NotificationOptions) => string;
    clearAllNotifications: () => void;
    clearNotificationsByType: (type: NotificationType) => void;
    hasCustoms: () => boolean;
    hasErrors: () => boolean;
    hasId: (id: string) => boolean;
    hasSuccesses: () => boolean;
    notifications: ManagedNotification[];
    numCustoms: () => number;
    numErrors: () => number;
    numSuccesses: () => number;
    removeNotification: (id: string) => void;
    updateNotification: (id: string, message: string | object | object[], optionsUpdate?: Partial<NotificationOptions>) => void;
}

let notificationIdCounter = 0;
const generateNotificationId = (): string => `notif-${notificationIdCounter++}-${Math.random().toString(36).substring(2, 9)}`;

// ---=== Notification Store Implementation ===--- //

function createNotificationStore() {
    let notifications: ManagedNotification[] = [];
    const listeners = new Set<() => void>();

    const emitChange = () => {
        for (const listener of listeners) {
            listener();
        }
    }

    const removeNotificationById = (id: string, isAutomaticRemoval = false) => {
        const notification = notifications.find(n => n.id === id);
        if (!notification) return;

        // Clear any existing timers
        if (notification.timerId) {
            clearTimeout(notification.timerId);
        }
        if (notification.fadeTimeoutId) {
            clearTimeout(notification.fadeTimeoutId);
        }

        if (isAutomaticRemoval && notification.options?.removeFadeDuration) {
            const fadeDuration = notification.options.removeFadeDuration || 0.5;
            notifications = notifications.map(n => {
                if (n.id === id) {
                    const element = document.getElementById(id);
                    if (element) {
                        element.style.transition = `opacity ${fadeDuration}s ease-out`;
                        element.style.opacity = "0";
                    }
                    return { ...n, isRemoving: true };
                }
                return n;
            });
            emitChange();

            // Remove after fade completes
            const fadeTimeoutId = window.setTimeout(() => {
                notifications = notifications.filter(n => n.id !== id);
                emitChange();
            }, fadeDuration * 1000);

            // Update the notification with the fade timeout ID
            notifications = notifications.map(n => {
                if (n.id === id) {
                    return { ...n, fadeTimeoutId };
                }
                return n;
            });
        }
        else {
            // Immediate removal
            const initialLength = notifications.length;
            notifications = notifications.filter(n => n.id !== id);
            if (notifications.length < initialLength) {
                emitChange();
            }
        }
    }

    const setupDurationTimer = (notification: ManagedNotification) => {
        if (notification.options?.duration && notification.options.duration > 0) {
            const timerId = window.setTimeout(() => {
                removeNotificationById(notification.id, true);
            }, notification.options.duration * 1000);

            // Update the notification with the timer ID
            notifications = notifications.map(n => {
                if (n.id === notification.id) {
                    return { ...n, timerId };
                }
                return n;
            });
        }
    };

    return {
        addNotification(notification: ManagedNotification) {
            // Set default removeFadeDuration if not specified
            const finalOptions = {
                ...notification.options,
                removeFadeDuration: notification.options?.removeFadeDuration ?? 0.5
            };

            const finalNotification = {
                ...notification,
                options: finalOptions
            };

            notifications = [...notifications, finalNotification];
            emitChange();

            // Setup duration timer after adding to store
            setupDurationTimer(finalNotification);
        },
        removeNotification: (id: string) => removeNotificationById(id, false),
        updateNotification(id: string, message: string | object | object[], optionsUpdate?: Partial<NotificationOptions>) {
            let found = false;
            notifications = notifications.map(n => {
                if (n.id === id) {
                    found = true;

                    // Clear existing timer if duration is being updated or if optionsUpdate is provided
                    if (n.timerId && (optionsUpdate?.duration !== undefined)) {
                        clearTimeout(n.timerId);
                    }

                    // Handle case where optionsUpdate is undefined/null
                    const newOptions = optionsUpdate
                        ? {
                            ...n.options,
                            ...optionsUpdate,
                            removeFadeDuration: optionsUpdate.removeFadeDuration ?? n.options?.removeFadeDuration ?? 0.5
                        }
                        : {
                            ...n.options,
                            removeFadeDuration: n.options?.removeFadeDuration ?? 0.5
                        } as NotificationOptions;

                    let updatedComponent: React.ReactElement;

                    const commonCloseHandler = () => removeNotificationById(n.id, false);

                    // Create the appropriate component based on the notification type
                    switch (n.type) {
                        case "error":
                            updatedComponent = <ErrorNotification id={n.id} key={n.id} message={message ?? n.message} options={newOptions} onClose={commonCloseHandler} />;
                            break;
                        case "success":
                            updatedComponent = <SuccessNotification id={n.id} key={n.id} message={message ?? n.message} options={newOptions} onClose={commonCloseHandler} />;
                            break;
                        case "custom":
                            updatedComponent = <CustomNotification id={n.id} key={n.id} message={message ?? n.message} options={newOptions} onClose={commonCloseHandler} />;
                            break;
                        default:
                            throw new Error(`Unknown notification type: ${n.type}`);
                    }

                    const updatedNotification = {
                        ...n,
                        message: message ?? n.message,
                        options: newOptions,
                        component: updatedComponent,
                        timerId: undefined
                    };

                    // Only set up a new timer if duration is specified and > 0
                    if (newOptions.duration && newOptions.duration > 0) {
                        updatedNotification.timerId = window.setTimeout(() => {
                            removeNotificationById(id, true);
                        }, newOptions.duration * 1000);
                    }

                    return updatedNotification;
                }
                return n;
            });

            if (found) {
                emitChange();
            }
            return found;
        },
        clearAll() {
            // Clear all timers before clearing notifications
            notifications.forEach(n => {
                if (n.timerId) clearTimeout(n.timerId);
                if (n.fadeTimeoutId) clearTimeout(n.fadeTimeoutId);
            });
            notifications = [];
            emitChange();
        },
        clearByType(type: NotificationType) {
            // Clear timers for notifications being removed
            notifications.forEach(n => {
                if (n.type === type) {
                    if (n.timerId) clearTimeout(n.timerId);
                    if (n.fadeTimeoutId) clearTimeout(n.fadeTimeoutId);
                }
            });
            notifications = notifications.filter(n => n.type !== type);
            emitChange();
        },
        // --- Subscription methods for useSyncExternalStore ---
        subscribe(listener: () => void) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot() {
            return notifications;
        }
    };
}

type NotificationStore = ReturnType<typeof createNotificationStore>;

const NotificationStoreContext = createContext<NotificationStore | null>(null);

// ---=== NotificationManagerProvider ===--- //

export interface NotificationManagerProviderProps {
    children: ReactNode;
}

export const NotificationManagerProvider: React.FC<NotificationManagerProviderProps> = ({ children }) => {
    // Create the store instance once using a ref
    const storeRef = useRef<NotificationStore | null>(null);
    if (!storeRef.current) {
        storeRef.current = createNotificationStore();
    }

    return (
        <NotificationStoreContext.Provider value={storeRef.current}>
            {children}
            <NotificationContainer />
        </NotificationStoreContext.Provider>
    );
};

// ---=== Custom Hook ===--- //

export const useNotificationManager = (): NotificationManagerAPI => {
    const store = useContext(NotificationStoreContext);
    if (!store) {
        throw new Error("useNotificationManager must be used within a NotificationManagerProvider");
    }

    // useSyncExternalStore subscribes to the store and returns a guaranteed-fresh snapshot of state.
    // This is the modern React way to handle external state and prevent tearing.
    const notifications = useSyncExternalStore(store.subscribe, store.getSnapshot);

    const removeNotification = store.removeNotification;

    // We use useMemo to create the public API. It recalculates whenever `notifications` change,
    // ensuring all derived data and functions are always based on the latest state.
    return useMemo(() => {
        const addNotification = (type: NotificationType, message: string | object | object[], options?: NotificationOptions): string => {
            const id = generateNotificationId();
            let notificationComponent: React.ReactElement;
            let managedNotificationPayload: Omit<ManagedNotification, "component">;

            const commonCloseHandler = () => removeNotification(id);

            // Added block scopes {} to each case to prevent 'no-case-declarations' error.
            switch (type) {
                case "error": {
                    notificationComponent = <ErrorNotification id={id} key={id} message={message} options={options} onClose={commonCloseHandler} />;
                    managedNotificationPayload = { id, type, message, options };
                    break;
                }
                case "success": {
                    notificationComponent = <SuccessNotification id={id} key={id} message={message} options={options} onClose={commonCloseHandler} />;
                    managedNotificationPayload = { id, type, message, options };
                    break;
                }
                case "custom": {
                    notificationComponent = <CustomNotification id={id} key={id} message={message} options={options} onClose={commonCloseHandler} />;
                    managedNotificationPayload = { id, type, message, options };
                    break;
                }
                default:
                    throw new Error(`Unknown notification type: ${type}`);
            }
            store.addNotification({ ...managedNotificationPayload, component: notificationComponent });
            return id;
        };

        const updateNotification = (id: string, message: string | object | object[], optionsUpdate: Partial<NotificationOptions>) => {
            const found = store.updateNotification(id, message, optionsUpdate);
            if (!found) {
                console.warn(`Notification with id "${id}" not found for update.`);
            }
        };

        const getCurrentNotifications = () => store.getSnapshot();

        return {
            addCustom: (message, options) => message && addNotification("custom", message, options),
            addError: (message, options) => message && addNotification("error", message, options),
            addSuccess: (message, options) => message && addNotification("success", message, options),
            clearAllNotifications: store.clearAll,
            clearNotificationsByType: store.clearByType,
            hasCustoms: () => getCurrentNotifications().some(n => n.type === "custom"),
            hasErrors: () => getCurrentNotifications().some(n => n.type === "error"),
            hasId: (id: string) => getCurrentNotifications().some(n => n.id === id),
            hasSuccesses: () => getCurrentNotifications().some(n => n.type === "success"),
            notifications,
            numCustoms: () => getCurrentNotifications().filter(n => n.type === "custom").length,
            numErrors: () => getCurrentNotifications().filter(n => n.type === "error").length,
            numSuccesses: () => getCurrentNotifications().filter(n => n.type === "success").length,
            removeNotification: store.removeNotification,
            updateNotification
        };
    }, [notifications, store, removeNotification]);
};

// ---=== Display Component (Consumer) ===--- //
// It's cleaner to have a dedicated component for rendering the list.
// It subscribes to the hook and re-renders when notifications change.
export const NotificationContainer: React.FC = () => {
    const { notifications } = useNotificationManager();

    if (notifications.length === 0) {
        return null;
    }

    return (
        <div
            id="notification-manager-container"
            className={"z-199 fixed top-[32px] right-0 left-[12px] m-[12px] flex flex-col items-end gap-2"}
            aria-live="assertive"
        >
            <div className="w-full flex flex-col items-end gap-2">
                {
                    notifications.map((notification) => (
                        <div key={notification.id} className="max-w-full">
                            {notification.component}
                        </div>
                    ))
                }
            </div>
        </div>
    );
};